import { ErrorMsg } from '../../types/ViewModel/ErrorMsg';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { RequiredConditionalMandatory } from '../../types/ViewModel/RequiredConditionalMandatory';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { EventTypeGroup } from '../../types/ViewModel/EventTypeGroup';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import useEventType from '../../app/useEventType';
import { EventType } from '../../types/ViewModel/EventType';
import useRotation from '../../app/useRotation';
import useEvent from '../../app/useEvent';
import { useRotationStore } from '../../context/RotationContext';
import eventtypecommon from './eventtypecommon';
import eventTypeGroupChangeLogUtils from './eventTypeGroupChangeLogUtils';
import { isDateAfter, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { compareActualEventDateTime, compareActualEventDateTimeWithoutSecMs, getDateWithoutSecMs } from './eventDateUtils';
import { isCargoLevelEvent, canBypassValidationForRetroactiveCargo, isSospOrSeaEvent } from './retroactiveCargoEventUtils';

export default () => {
    const { updatedRotation } = useRotationStore();
    const { getEventType, getEventTypeGroup } = useEventType();
    const { getPortCall, getBerthCall, getCargoOperation } = useRotation();
    const { filterOnContext, filterSameEventTypeGroupAndLevel, filterOnCrossNode, filterOnNodeIndex } = useEvent();
    const {
        getVoyageNameOrSea,
        getParentEventsSameSequence,
        isRequiredEventTypeInSameEventTypeGroup,
        hasNotAfterOk,
        sortOrderedEvents,
        hasEventGroupDependency,
        findMissingEventTypeInSequence,
        isEventOnSelectedNode,
        getParentEvents,
        hasConcurrentParentInCompleteTree,
        getDepSeqIdentity,
        generateEventNameWithDependency,
        generateEventNameWithDependencyAndDate,
        generateDisplayName,
        getEventTypeName,
        sortOrderedEventTypes,
    } = eventtypecommon();
    const { isMultiple, isConcurrent, getRequiredPreviousTypeId, getNotAfterEventTypeId } = eventTypeGroupChangeLogUtils();

    // Verify time and date for parent event type groups, for new events
    const isParentSequenceOkForNew = (
        eventArray: TankersEvent[],
        checkEventType: EventType,
        eventTypeData: EventTypeData,
        checkDate: Date,
        requiredPreviousTypeId: number,
        node: IRotationNode,
        isRetroactiveModeEnabled?: boolean
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        const requiredPreviousEventType: TankersEvent | null =
            eventArray.find(
                (ev) =>
                    (!checkEventType.parentSequenceNumber || checkEventType.parentSequenceNumber === ev.concurrentSequenceNumber) &&
                    ev.eventTypeId === requiredPreviousTypeId
            ) ?? null;

        // Check if we can bypass validation for retroactive cargo events
        if (requiredPreviousEventType && isRetroactiveModeEnabled !== undefined) {
            // Create a minimal mock event for bypass validation
            const mockCheckEvent = {
                eventTypeId: checkEventType.eventTypeId,
                eventLevel: checkEventType.levelId,
                cargoOperationId: checkEventType.levelId === RotationTreeEnum.CargoOperation ? (node as any).cargoOperationId : undefined,
                portCallId: (node as any).portCallId || (node.portCall ? node.portCall.portCallId : undefined),
            } as unknown as TankersEvent;

            if (canBypassValidationForRetroactiveCargo(mockCheckEvent, requiredPreviousEventType, eventArray, isRetroactiveModeEnabled)) {
                // Bypass validation for retroactive cargo events
                return errorMsg; // Return no error
            }
        }
        if (requiredPreviousEventType && requiredPreviousEventType.eventType) {
            const evInParentGroupSameSeqArray = getParentEventsSameSequence(
                eventArray,
                checkEventType,
                requiredPreviousEventType.eventType.eventTypeGroupId
            );
            if (evInParentGroupSameSeqArray && evInParentGroupSameSeqArray.length > 0) {
                let reqEvInSequence: TankersEvent | null = null;
                if (checkEventType.eventTypeGroupSequenceDependencyIdentity) {
                    reqEvInSequence =
                        evInParentGroupSameSeqArray.find(
                            (ev) =>
                                checkEventType.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity &&
                                ev.eventTypeId === requiredPreviousTypeId
                        ) ?? null;
                    if (reqEvInSequence && reqEvInSequence.eventDate && reqEvInSequence.eventDate.actualDateTimeUtc) {
                        if (isDateAfter(getDateWithoutSecMs(reqEvInSequence.eventDate.actualDateTimeUtc), checkDate) && reqEvInSequence.eventType) {
                            errorMsg.error = true;
                            errorMsg.message =
                                "'" +
                                checkEventType.name +
                                "' must be after '" +
                                reqEvInSequence.eventType.name +
                                generateDisplayName(reqEvInSequence.dependencyDisplayName);
                            errorMsg.notValidatedEvent = reqEvInSequence;
                            return errorMsg;
                        }
                    }
                } else {
                    const depSeqId = getDepSeqIdentity(checkEventType);
                    if (depSeqId) {
                        reqEvInSequence =
                            evInParentGroupSameSeqArray.find(
                                (ev) => depSeqId === ev.eventTypeGroupSequenceIdentity && ev.eventTypeId === requiredPreviousTypeId
                            ) ?? null;
                    } else {
                        const ind = findMissingEventTypeInSequence(evInParentGroupSameSeqArray);
                        if (ind !== -1) {
                            const seqId = evInParentGroupSameSeqArray[ind].eventTypeGroupSequenceIdentity;
                            reqEvInSequence =
                                evInParentGroupSameSeqArray.find(
                                    (ev) => ev.eventTypeGroupSequenceIdentity === seqId && ev.eventTypeId === requiredPreviousTypeId
                                ) ?? null;
                        } else {
                            // Find latest event for completed parent event sequences
                            const evParentSorted = evInParentGroupSameSeqArray.sort((a, b) => compareActualEventDateTime(a, b, false));
                            reqEvInSequence =
                                evInParentGroupSameSeqArray.find(
                                    (ev) =>
                                        ev.eventTypeGroupSequenceIdentity ===
                                            evParentSorted[evParentSorted.length - 1].eventTypeGroupSequenceIdentity &&
                                        ev.eventTypeId === requiredPreviousTypeId
                                ) ?? null;
                        }
                    }
                    if (reqEvInSequence && reqEvInSequence.eventType) {
                        if (
                            reqEvInSequence.eventDate &&
                            reqEvInSequence.eventDate.actualDateTimeUtc &&
                            isDateAfter(getDateWithoutSecMs(reqEvInSequence.eventDate.actualDateTimeUtc), checkDate)
                        ) {
                            errorMsg.error = true;
                            errorMsg.message =
                                "'" +
                                checkEventType.name +
                                "' must be after '" +
                                reqEvInSequence.eventType.name +
                                generateDisplayName(reqEvInSequence.dependencyDisplayName);
                            errorMsg.notValidatedEvent = reqEvInSequence;
                            return errorMsg;
                        }

                        // For example parent = Loading master, child = Key meeting carried out
                        if (
                            requiredPreviousEventType.eventType &&
                            requiredPreviousEventType.eventType.eventTypeGroup &&
                            isMultiple(requiredPreviousEventType.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                            !hasConcurrentParentInCompleteTree(checkEventType, eventTypeData, node)
                        ) {
                            const evInParentGroupArray = eventArray
                                .filter(
                                    (ev: TankersEvent) =>
                                        ev.eventType &&
                                        requiredPreviousEventType.eventType &&
                                        ev.eventType.eventTypeGroupId === requiredPreviousEventType.eventType.eventTypeGroupId
                                )
                                .sort((a, b) => compareActualEventDateTime(a, b, false));
                            errorMsg = isSequenceOkNew(evInParentGroupArray, checkEventType, checkDate);
                            if (errorMsg.error) {
                                return errorMsg;
                            }
                        }
                    }
                }
            }
        }
        return errorMsg;
    };

    // Verify time and date for parent event type groups, for existing events
    const isParentSequenceOkForUpdate = (
        eventArray: TankersEvent[],
        checkEvent: TankersEvent,
        eventTypeData: EventTypeData,
        checkDate: Date,
        requiredPreviousTypeId: number,
        notAfterTypeId: number | null,
        node: IRotationNode,
        isRetroactiveModeEnabled?: boolean
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        const requiredPreviousEventType: TankersEvent | null =
            eventArray.find((ev) => {
                return ev.eventTypeId === requiredPreviousTypeId;
            }) ?? null;

        // Check if we can bypass validation for retroactive cargo events
        if (
            requiredPreviousEventType &&
            canBypassValidationForRetroactiveCargo(checkEvent, requiredPreviousEventType, eventArray, isRetroactiveModeEnabled)
        ) {
            // Bypass validation for retroactive cargo events
            return errorMsg; // Return no error
        }
        if (requiredPreviousEventType && checkEvent.eventTypeGroupSequenceDependencyIdentity) {
            const evInParentGroupSameSeqArray = eventArray.filter((ev: TankersEvent) => {
                return (
                    ev.eventType &&
                    requiredPreviousEventType.eventType &&
                    ev.eventType.eventTypeGroupId === requiredPreviousEventType.eventType.eventTypeGroupId &&
                    checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                );
            });
            if (evInParentGroupSameSeqArray && evInParentGroupSameSeqArray.length > 0) {
                let reqPrevEvInSequence: TankersEvent | null = null;
                let displayName: string | null = null;
                reqPrevEvInSequence = evInParentGroupSameSeqArray.find((ev) => ev.eventTypeId === requiredPreviousTypeId) ?? null;
                if (reqPrevEvInSequence) {
                    if (reqPrevEvInSequence.dependencyDisplayName) {
                        displayName = ' ' + reqPrevEvInSequence.dependencyDisplayName;
                    }
                    if (
                        checkEvent.eventType &&
                        reqPrevEvInSequence.eventType &&
                        isDateAfter(getDateWithoutSecMs(reqPrevEvInSequence.eventDate.actualDateTimeUtc), checkDate)
                    ) {
                        const prevLocalTime = reqPrevEvInSequence.eventDate.actualDateTimeLocal;
                        const checkLocalTime = checkEvent.eventDate?.actualDateTimeLocal;

                        // Fallback: If local times suggest correct order, accept it and skip UTC validation failure
                        // This handles corrupted UTC in database while still preventing actual time sequence violations
                        if (
                            prevLocalTime &&
                            checkLocalTime &&
                            !isDateAfter(getDateWithoutSecMs(prevLocalTime), getDateWithoutSecMs(checkLocalTime))
                        ) {
                            return errorMsg;
                        }

                        errorMsg.error = true;
                        errorMsg.message =
                            "'" +
                            checkEvent.eventType.name +
                            "' must be after '" +
                            reqPrevEvInSequence.eventType.name +
                            generateDisplayName(displayName);
                        errorMsg.notValidatedEvent = reqPrevEvInSequence;
                        return errorMsg;
                    }

                    // New code, for example parent = Loading master, child = Key meeting carried out
                    if (
                        requiredPreviousEventType.eventType &&
                        requiredPreviousEventType.eventType.eventTypeGroup &&
                        isMultiple(requiredPreviousEventType.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                        !hasConcurrentParentInCompleteTree(checkEvent.eventType, eventTypeData, node)
                    ) {
                        const notAFterEventType: TankersEvent | null =
                            evInParentGroupSameSeqArray.find((ev) => ev.eventTypeId === notAfterTypeId) ?? null;
                        if (notAFterEventType) {
                            if (
                                checkEvent.eventType &&
                                notAFterEventType.eventType &&
                                isDateBefore(getDateWithoutSecMs(notAFterEventType.eventDate.actualDateTimeUtc), checkDate)
                            ) {
                                displayName = null;
                                if (notAFterEventType.dependencyDisplayName) {
                                    displayName = ' ' + notAFterEventType.dependencyDisplayName;
                                }
                                errorMsg.error = true;
                                errorMsg.message =
                                    "'" +
                                    checkEvent.eventType.name +
                                    "' must be before '" +
                                    notAFterEventType.eventType.name +
                                    generateDisplayName(displayName);
                                errorMsg.notValidatedEvent = notAFterEventType;
                                return errorMsg;
                            }
                        } else {
                            const evInParentGroupArray = eventArray
                                .filter(
                                    (ev: TankersEvent) =>
                                        ev.eventType &&
                                        requiredPreviousEventType.eventType &&
                                        ev.eventType.eventTypeGroupId === requiredPreviousEventType.eventType.eventTypeGroupId &&
                                        checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                                )
                                .sort((a, b) => compareActualEventDateTime(a, b, false));
                            errorMsg = isSequenceOk(evInParentGroupArray, checkEvent, checkDate);
                            if (errorMsg.error) {
                                return errorMsg;
                            }
                        }
                    }
                }
                // Find parent
                const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall);
                if (checkEvent.eventType && checkEvent.eventType.eventTypeGroup && reqPrevTypeId) {
                    // Find one parent event
                    const reqParentEv: TankersEvent | null =
                        eventArray.find(
                            (ev: TankersEvent) =>
                                checkEvent.eventType &&
                                checkEvent.eventType.eventTypeGroup &&
                                ev.eventTypeId === reqPrevTypeId &&
                                ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceDependencyIdentity
                        ) ?? null;
                    // Check that parent is not concurrent and multiple
                    if (
                        reqParentEv &&
                        reqParentEv.eventType &&
                        reqParentEv.eventType.eventTypeGroup &&
                        !isConcurrent(reqParentEv.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                        isMultiple(reqParentEv.eventType.eventTypeGroup, node.firstTimeInPortCall)
                    ) {
                        // Find all events in the parent event type group, sort by date
                        const sameParentEventList = eventArray
                            .filter(
                                (ev: TankersEvent) =>
                                    ev.eventType &&
                                    ev.eventType.eventTypeGroup &&
                                    reqParentEv.eventType &&
                                    reqParentEv.eventType.eventTypeGroup &&
                                    ev.eventType.eventTypeGroup.eventTypeGroupId === reqParentEv.eventType.eventTypeGroup.eventTypeGroupId &&
                                    ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceDependencyIdentity
                            )
                            .sort((a, b) => compareActualEventDateTime(a, b, false));
                        //evItem => getDateWithoutSecMs(evItem.eventDate.actualDateTimeUtc));
                        if (sameParentEventList && sameParentEventList.length > 0) {
                            // Check sequence of parent
                            const indSequence = findMissingEventTypeInSequence(sameParentEventList);
                            if (indSequence !== -1) {
                                if (isDateAfter(getDateWithoutSecMs(sameParentEventList[indSequence].eventDate.actualDateTimeUtc), checkDate)) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        generateEventNameWithDependency(checkEvent) +
                                        ' must be after previous ' +
                                        generateEventNameWithDependencyAndDate(sameParentEventList[indSequence]);
                                    errorMsg.notValidatedEvent = sameParentEventList[indSequence];
                                } else {
                                    if (indSequence < sameParentEventList.length - 1) {
                                        if (
                                            isDateBefore(
                                                getDateWithoutSecMs(sameParentEventList[indSequence + 1].eventDate.actualDateTimeUtc),
                                                checkDate
                                            )
                                        ) {
                                            errorMsg.error = true;
                                            errorMsg.message =
                                                generateEventNameWithDependency(checkEvent) +
                                                ' must be before next ' +
                                                generateEventNameWithDependencyAndDate(sameParentEventList[indSequence + 1]);
                                            errorMsg.notValidatedEvent = sameParentEventList[indSequence + 1];
                                        }
                                    }
                                }
                            } else {
                                if (sameParentEventList.length > 0) {
                                    const seqEvList = sameParentEventList.filter(
                                        (ev: TankersEvent) =>
                                            ev.eventTypeGroupSequenceIdentity ===
                                            sameParentEventList[sameParentEventList.length - 1].eventTypeGroupSequenceIdentity
                                    );
                                    const notAfterEv = seqEvList.find(
                                        (ev: TankersEvent) =>
                                            checkEvent.eventType &&
                                            checkEvent.eventType.eventTypeGroup &&
                                            getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                                            ev.eventTypeId === getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
                                    );
                                    if (notAfterEv) {
                                        if (isDateAfter(checkDate, getDateWithoutSecMs(notAfterEv.eventDate.actualDateTimeUtc))) {
                                            errorMsg.error = true;
                                            errorMsg.message =
                                                generateEventNameWithDependency(checkEvent) +
                                                ' must be before latest ' +
                                                generateEventNameWithDependencyAndDate(notAfterEv);
                                            errorMsg.notValidatedEvent = notAfterEv;
                                        }
                                    } else {
                                        const reqPrevEvent = seqEvList.find(
                                            (ev: TankersEvent) =>
                                                checkEvent.eventType &&
                                                checkEvent.eventType.eventTypeGroup &&
                                                reqPrevTypeId &&
                                                ev.eventTypeId === reqPrevTypeId
                                        );
                                        if (reqPrevEvent) {
                                            if (isDateBefore(checkDate, getDateWithoutSecMs(reqPrevEvent.eventDate.actualDateTimeUtc))) {
                                                errorMsg.error = true;
                                                errorMsg.message =
                                                    generateEventNameWithDependency(checkEvent) +
                                                    ' must be after ' +
                                                    generateEventNameWithDependencyAndDate(reqPrevEvent);
                                                errorMsg.notValidatedEvent = reqParentEv;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                // }
            }
        }
        return errorMsg;
    };

    const eventTypeNameOrUnknown = (eventType: EventType | null): string => {
        if (eventType) {
            return eventType.name;
        }
        return '???';
    };

    const isMultipleNotConcurrentSeqOkNew = (
        eventArray: TankersEvent[],
        checkEventType: EventType,
        checkDate: Date,
        node: IRotationNode
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        let evInGroupArray = filterSameEventTypeGroupAndLevel(eventArray, checkEventType).sort((a, b) => compareActualEventDateTime(a, b, false));

        if (checkEventType.parentSequenceNumber) {
            evInGroupArray = evInGroupArray.filter((ev) => ev.concurrentSequenceNumber === checkEventType.parentSequenceNumber);
        }

        const reqPrevTypeId = getRequiredPreviousTypeId(checkEventType.eventTypeGroup, node.firstTimeInPortCall);
        if (evInGroupArray && evInGroupArray.length > 0) {
            // If checkEvent has parent, which has selected parent dropdown
            if (checkEventType.eventTypeGroup && reqPrevTypeId && checkEventType.eventTypeGroupSequenceDependencyIdentity) {
                const sameParentEventList: TankersEvent[] = eventArray.filter((ev) => {
                    return (
                        ev.eventType &&
                        ev.eventType.eventTypeGroup &&
                        checkEventType.eventTypeGroup &&
                        ev.eventType.eventTypeGroup.eventTypeGroupId === checkEventType.eventTypeGroup.eventTypeGroupId &&
                        ev.eventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId ===
                            checkEventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId &&
                        ev.eventTypeGroupSequenceDependencyIdentity === checkEventType.eventTypeGroupSequenceDependencyIdentity
                    );
                });
                if (sameParentEventList) {
                    const indSequence = findMissingEventTypeInSequence(sameParentEventList);
                    if (indSequence !== -1) {
                        if (isDateAfter(getDateWithoutSecMs(sameParentEventList[indSequence].eventDate.actualDateTimeUtc), checkDate)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                getEventTypeName(checkEventType) +
                                " must be after previous '" +
                                eventTypeNameOrUnknown(sameParentEventList[indSequence].eventType) +
                                "'";
                            errorMsg.notValidatedEvent = sameParentEventList[indSequence];
                        } else {
                            if (indSequence < sameParentEventList.length - 1) {
                                if (isDateBefore(getDateWithoutSecMs(sameParentEventList[indSequence + 1].eventDate.actualDateTimeUtc), checkDate)) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        getEventTypeName(checkEventType) +
                                        " must be before next '" +
                                        eventTypeNameOrUnknown(sameParentEventList[indSequence + 1].eventType) +
                                        "'";
                                    errorMsg.notValidatedEvent = sameParentEventList[indSequence + 1];
                                }
                            }
                        }
                    } else {
                        if (sameParentEventList.length > 0) {
                            if (
                                isDateAfter(
                                    getDateWithoutSecMs(sameParentEventList[sameParentEventList.length - 1].eventDate.actualDateTimeUtc),
                                    checkDate
                                )
                            ) {
                                errorMsg.error = true;
                                errorMsg.message =
                                    getEventTypeName(checkEventType) +
                                    " must be after latest '" +
                                    eventTypeNameOrUnknown(sameParentEventList[sameParentEventList.length - 1].eventType) +
                                    "'";
                                errorMsg.notValidatedEvent = sameParentEventList[sameParentEventList.length - 1];
                            }
                        }
                    }
                }
            } else {
                let sameSeqEvList: TankersEvent[] = [];
                if (checkEventType.eventTypeGroupSequenceDependencyIdentity) {
                    sameSeqEvList = eventArray.filter((ev) => {
                        return (
                            ev.eventType &&
                            ev.eventTypeGroupSequenceDependencyIdentity === checkEventType.eventTypeGroupSequenceDependencyIdentity &&
                            ev.eventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId ===
                                checkEventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId &&
                            ev.eventType.eventTypeGroupId === checkEventType.eventTypeGroupId
                        );
                    });
                } else {
                    sameSeqEvList = evInGroupArray;
                }
                errorMsg = isSequenceOkNew(sameSeqEvList, checkEventType, checkDate);
                if (errorMsg.error) {
                    return errorMsg;
                }
                if (checkEventType.eventTypeGroup && reqPrevTypeId && checkEventType.eventTypeGroupSequenceDependencyIdentity) {
                    const requiredPreviousEventType: TankersEvent | null =
                        eventArray.find(
                            (ev: TankersEvent) =>
                                checkEventType.eventTypeGroup &&
                                ev.eventTypeId === reqPrevTypeId &&
                                ev.eventTypeGroupSequenceIdentity === checkEventType.eventTypeGroupSequenceDependencyIdentity
                        ) ?? null;
                    if (
                        requiredPreviousEventType &&
                        requiredPreviousEventType.eventType &&
                        requiredPreviousEventType.eventType.eventTypeGroup &&
                        !isConcurrent(requiredPreviousEventType.eventType.eventTypeGroup, node.firstTimeInPortCall)
                    ) {
                        const sameParentEventList = eventArray
                            .filter(
                                (ev: TankersEvent) =>
                                    ev.eventType &&
                                    ev.eventType.eventTypeGroup &&
                                    requiredPreviousEventType.eventType &&
                                    requiredPreviousEventType.eventType.eventTypeGroup &&
                                    ev.eventType.eventTypeGroup.eventTypeGroupId ===
                                        requiredPreviousEventType.eventType.eventTypeGroup.eventTypeGroupId &&
                                    ev.eventTypeGroupSequenceDependencyIdentity === requiredPreviousEventType.eventTypeGroupSequenceDependencyIdentity
                            )
                            .sort((a, b) => compareActualEventDateTime(a, b, false));
                        //evItem => getDateWithoutSecMs(evItem.eventDate.actualDateTimeUtc));
                        if (sameParentEventList && sameParentEventList.length > 0) {
                            errorMsg = isSequenceOkNew(sameParentEventList, checkEventType, checkDate);
                            if (errorMsg.error) {
                                return errorMsg;
                            }
                        }
                    }
                }
            }
        }
        return errorMsg;
    };

    const isSequenceOk = (sameSeqEvList: TankersEvent[], checkEvent: TankersEvent, checkDate: Date): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);
        if (sameSeqEvList && sameSeqEvList.length > 0) {
            const indSequence = findMissingEventTypeInSequence(sameSeqEvList);
            if (indSequence !== -1) {
                if (isDateAfter(getDateWithoutSecMs(sameSeqEvList[indSequence].eventDate.actualDateTimeUtc), checkDate)) {
                    errorMsg.error = true;
                    errorMsg.message =
                        generateEventNameWithDependency(checkEvent) +
                        ' must be after previous ' +
                        generateEventNameWithDependencyAndDate(sameSeqEvList[indSequence]);
                    errorMsg.notValidatedEvent = sameSeqEvList[indSequence];
                } else {
                    if (indSequence < sameSeqEvList.length - 1) {
                        if (isDateBefore(getDateWithoutSecMs(sameSeqEvList[indSequence + 1].eventDate.actualDateTimeUtc), checkDate)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) +
                                ' must be before next ' +
                                generateEventNameWithDependencyAndDate(sameSeqEvList[indSequence + 1]);
                            errorMsg.notValidatedEvent = sameSeqEvList[indSequence + 1];
                        }
                    }
                }
            } else {
                if (sameSeqEvList.length > 0) {
                    if (isDateAfter(getDateWithoutSecMs(sameSeqEvList[sameSeqEvList.length - 1].eventDate.actualDateTimeUtc), checkDate)) {
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) +
                            ' must be after latest ' +
                            generateEventNameWithDependencyAndDate(sameSeqEvList[sameSeqEvList.length - 1]);
                        errorMsg.notValidatedEvent = sameSeqEvList[sameSeqEvList.length - 1];
                    }
                }
            }
        }
        return errorMsg;
    };

    const isSequenceOkNew = (sameSeqEvList: TankersEvent[], checkEventType: EventType, checkDate: Date): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);
        if (sameSeqEvList && sameSeqEvList.length > 0) {
            const indSequence = findMissingEventTypeInSequence(sameSeqEvList);
            if (indSequence !== -1) {
                if (isDateAfter(getDateWithoutSecMs(sameSeqEvList[indSequence].eventDate.actualDateTimeUtc), checkDate)) {
                    errorMsg.error = true;
                    errorMsg.message =
                        getEventTypeName(checkEventType) +
                        ' must be after previous ' +
                        generateEventNameWithDependencyAndDate(sameSeqEvList[indSequence]);
                    errorMsg.notValidatedEvent = sameSeqEvList[indSequence];
                } else {
                    if (indSequence < sameSeqEvList.length - 1) {
                        if (isDateBefore(getDateWithoutSecMs(sameSeqEvList[indSequence + 1].eventDate.actualDateTimeUtc), checkDate)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                getEventTypeName(checkEventType) +
                                ' must be before next ' +
                                generateEventNameWithDependencyAndDate(sameSeqEvList[indSequence + 1]);
                            errorMsg.notValidatedEvent = sameSeqEvList[indSequence + 1];
                        }
                    }
                }
            } else {
                if (sameSeqEvList.length > 0) {
                    if (isDateAfter(getDateWithoutSecMs(sameSeqEvList[sameSeqEvList.length - 1].eventDate.actualDateTimeUtc), checkDate)) {
                        errorMsg.error = true;
                        errorMsg.message =
                            getEventTypeName(checkEventType) +
                            ' must be after latest ' +
                            generateEventNameWithDependencyAndDate(sameSeqEvList[sameSeqEvList.length - 1]);
                        errorMsg.notValidatedEvent = sameSeqEvList[sameSeqEvList.length - 1];
                    }
                }
            }
        }
        return errorMsg;
    };

    const isMultipleNotConcurrentSeqOkUpdate = (
        eventArray: TankersEvent[],
        checkEvent: TankersEvent,
        checkDate: Date,
        node: IRotationNode
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        const evInGroupArraySorted = filterSameEventTypeGroupAndLevel(eventArray, checkEvent.eventType).sort((a, b) =>
            compareActualEventDateTime(a, b, false)
        );
        if (evInGroupArraySorted && evInGroupArraySorted.length > 0) {
            // If checkEvent has parent
            if (
                checkEvent.eventType &&
                checkEvent.eventType.eventTypeGroup &&
                getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
            ) {
                const sameParentEventList = eventArray
                    .filter(
                        (ev: TankersEvent) =>
                            ev.eventType &&
                            ev.eventType.eventTypeGroup &&
                            checkEvent.eventType &&
                            checkEvent.eventType.eventTypeGroup &&
                            ev.eventType.eventTypeGroup.eventTypeGroupId === checkEvent.eventType.eventTypeGroup.eventTypeGroupId &&
                            ev.eventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId ===
                                checkEvent.eventType.eventTypeGroup.eventTypeLevel?.eventTypeLevelId &&
                            ev.eventTypeGroupSequenceDependencyIdentity === checkEvent.eventTypeGroupSequenceDependencyIdentity
                    )
                    .sort((a, b) => compareActualEventDateTime(a, b, false));

                if (checkEvent.eventTypeGroupSequenceDependencyIdentity && sameParentEventList && sameParentEventList.length > 0) {
                    const checkEvInd = sameParentEventList.findIndex((ev) => ev.eventId === checkEvent.eventId);
                    if (checkEvInd > -1) {
                        if (checkEvInd < sameParentEventList.length - 1) {
                            // Check the next event in the list
                            if (isDateBefore(getDateWithoutSecMs(sameParentEventList[checkEvInd + 1].eventDate.actualDateTimeUtc), checkDate)) {
                                errorMsg.error = true;
                                errorMsg.message =
                                    generateEventNameWithDependency(checkEvent) +
                                    " must be before next '" +
                                    sameParentEventList[checkEvInd + 1].eventType.name +
                                    "'";
                                errorMsg.notValidatedEvent = sameParentEventList[checkEvInd + 1];
                                return errorMsg;
                            }
                        }
                        if (checkEvInd > 0) {
                            // Check the previous event in the list
                            if (isDateAfter(getDateWithoutSecMs(sameParentEventList[checkEvInd - 1].eventDate.actualDateTimeUtc), checkDate)) {
                                errorMsg.error = true;
                                errorMsg.message =
                                    generateEventNameWithDependency(checkEvent) +
                                    " must be after previous '" +
                                    sameParentEventList[checkEvInd - 1].eventType.name +
                                    "'";
                                errorMsg.notValidatedEvent = sameParentEventList[checkEvInd - 1];
                                return errorMsg;
                            }
                            // Check event types dependent on the previous event
                            errorMsg = isAfterLatestChild(eventArray, sameParentEventList[checkEvInd - 1], checkDate, checkEvent, node);
                            if (errorMsg.error) {
                                return errorMsg;
                            }
                        }
                    } else {
                        // Parent dropdown has been updated
                        if (
                            isDateAfter(
                                getDateWithoutSecMs(sameParentEventList[sameParentEventList.length - 1].eventDate.actualDateTimeUtc),
                                checkDate
                            )
                        ) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) +
                                " must be after previous '" +
                                sameParentEventList[sameParentEventList.length - 1].eventType.name +
                                "'";
                            errorMsg.notValidatedEvent = sameParentEventList[sameParentEventList.length - 1];
                            return errorMsg;
                        }
                    }
                } else {
                    const evInd = evInGroupArraySorted.findIndex((ev) => ev.eventId === checkEvent.eventId);
                    if (evInd > 0) {
                        errorMsg = isAfterLatestChild(eventArray, evInGroupArraySorted[evInd - 1], checkDate, checkEvent, node);
                        if (errorMsg.error) {
                            return errorMsg;
                        }
                    }
                    if (evInd < evInGroupArraySorted.length - 1) {
                        if (isDateBefore(getDateWithoutSecMs(evInGroupArraySorted[evInd + 1].eventDate.actualDateTimeUtc), checkDate)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) +
                                " must be before next '" +
                                evInGroupArraySorted[evInd + 1].eventType.name +
                                "'";
                            errorMsg.notValidatedEvent = evInGroupArraySorted[evInd + 1];
                            return errorMsg;
                        }
                    }
                }
            } else {
                const evInd = evInGroupArraySorted.findIndex((ev) => ev.eventId === checkEvent.eventId);
                if (evInd > 0) {
                    errorMsg = isAfterLatestChild(eventArray, evInGroupArraySorted[evInd - 1], checkDate, checkEvent, node);
                    if (errorMsg.error) {
                        return errorMsg;
                    }
                }
                if (evInd < evInGroupArraySorted.length - 1) {
                    if (isDateBefore(getDateWithoutSecMs(evInGroupArraySorted[evInd + 1].eventDate.actualDateTimeUtc), checkDate)) {
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) +
                            " must be before next '" +
                            evInGroupArraySorted[evInd + 1].eventType.name +
                            "'";
                        errorMsg.notValidatedEvent = evInGroupArraySorted[evInd + 1];
                        return errorMsg;
                    }
                }
            }
        }
        return errorMsg;
    };

    const isAfterLatestChild = (
        eventArray: TankersEvent[],
        parentEvent: TankersEvent,
        checkDate: Date,
        checkEvent: TankersEvent,
        node: IRotationNode
    ): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);
        const latestEvent = findLatestChild(eventArray, parentEvent, node);
        if (latestEvent && isDateAfter(getDateWithoutSecMs(latestEvent.eventDate.actualDateTimeUtc), checkDate)) {
            errorMsg.error = true;
            if (parentEvent.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceIdentity) {
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) + " must be after '" + eventTypeNameOrUnknown(latestEvent.eventType) + "'";
                errorMsg.notValidatedEvent = latestEvent;
            } else if (
                parentEvent.eventType &&
                latestEvent.eventType &&
                parentEvent.eventType.eventTypeGroupId === latestEvent.eventType.eventTypeGroupId
            ) {
                errorMsg.message = generateEventNameWithDependency(checkEvent) + " must be after previous '" + latestEvent.eventType.name + "'";
                errorMsg.notValidatedEvent = latestEvent;
            } else if (latestEvent) {
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) +
                    " must be after '" +
                    eventTypeNameOrUnknown(latestEvent.eventType) +
                    "'" +
                    " for previous '" +
                    eventTypeNameOrUnknown(parentEvent.eventType) +
                    "'";
                errorMsg.notValidatedEvent = latestEvent;
            }
        }
        return errorMsg;
    };

    const findLatestChild = (eventArray: TankersEvent[], startEv: TankersEvent, node: IRotationNode): TankersEvent | null => {
        let keepGoing = true;
        let latestDate = getDateWithoutSecMs(startEv.eventDate.actualDateTimeUtc);
        let latestEvent: TankersEvent | null = null;
        let evSeqId = startEv.eventTypeGroupSequenceIdentity;
        while (keepGoing) {
            const evList = eventArray
                .filter(
                    (ev: TankersEvent) =>
                        ev.eventTypeGroupSequenceDependencyIdentity === evSeqId &&
                        getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall)
                )
                .sort((a, b) => compareActualEventDateTime(a, b, false));
            if (evList && evList.length > 0) {
                if (isDateAfter(getDateWithoutSecMs(evList[evList.length - 1].eventDate.actualDateTimeUtc), latestDate)) {
                    latestDate = getDateWithoutSecMs(evList[evList.length - 1].eventDate.actualDateTimeUtc);
                    latestEvent = evList[evList.length - 1];
                }
                evSeqId = evList[0].eventTypeGroupSequenceIdentity;
            } else {
                keepGoing = false;
            }
        }
        return latestEvent;
    };

    const sortEventsWithoutSecMs = (evList: TankersEvent[], desc: boolean): TankersEvent[] => {
        return evList.sort((a, b) => compareActualEventDateTimeWithoutSecMs(a, b, desc));
    };

    const createNodeName = (node: IRotationNode, ev: TankersEvent): string => {
        if (ev.portCallId && ev.eventTypeContextId) {
            switch (node.type) {
                case RotationTreeEnum.Sea: {
                    const portCallSea = getPortCall(updatedRotation, ev.portCallId, ev.eventTypeContextId);
                    return getVoyageNameOrSea(ev, portCallSea);
                }
                case RotationTreeEnum.PortCall: {
                    const portCall = getPortCall(updatedRotation, ev.portCallId, ev.eventTypeContextId);
                    return getVoyageNameOrSea(ev, portCall);
                }
                case RotationTreeEnum.BerthCall:
                    if (ev.berthCallId) {
                        const berthCall = getBerthCall(updatedRotation, ev.portCallId, ev.berthCallId);
                        if (berthCall) {
                            return " in berth call '" + berthCall.name + "'";
                        }
                    }
                    break;
                case RotationTreeEnum.CargoOperation:
                    if (ev.berthCallId && ev.cargoOperationId) {
                        const cargoOp = getCargoOperation(updatedRotation, ev.portCallId, ev.berthCallId, ev.cargoOperationId);
                        if (cargoOp) {
                            return " in cargo operation '" + cargoOp.name + "'";
                        }
                    }
                    break;
            }
        }
        return '';
    };

    const crossPortCallConditionPrevious = (
        fullEventArray: TankersEvent[],
        selectedNode: IRotationNode,
        evTypeName: string,
        checkDateUtc: Date,
        evTypeGroup: EventTypeGroup
    ): ErrorMsg | null => {
        const evListBefore = filterOnNodeIndex(selectedNode, fullEventArray, false);
        const evListToCheckBefore = filterOnCrossNode(evListBefore, evTypeGroup, true);
        if (evListToCheckBefore && evListToCheckBefore.length > 0) {
            const evListToCheckBeforeSorted = sortEventsWithoutSecMs(evListToCheckBefore, false);
            if (
                isDateBefore(
                    checkDateUtc,
                    getDateWithoutSecMs(evListToCheckBeforeSorted[evListToCheckBeforeSorted.length - 1].eventDate.actualDateTimeUtc)
                )
            ) {
                const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);
                errorMsg.error = true;
                errorMsg.message =
                    evTypeName +
                    ' must be after ' +
                    generateEventNameWithDependencyAndDate(evListToCheckBeforeSorted[evListToCheckBeforeSorted.length - 1]) +
                    createNodeName(selectedNode, evListToCheckBeforeSorted[evListToCheckBeforeSorted.length - 1]);
                errorMsg.notValidatedEvent = evListToCheckBeforeSorted[evListToCheckBeforeSorted.length - 1];
                return errorMsg;
            }
        }
        return null;
    };

    const crossPortCallConditionNext = (
        fullEventArray: TankersEvent[],
        selectedNode: IRotationNode,
        evTypeName: string,
        checkDateUtc: Date,
        evTypeGroup: EventTypeGroup
    ): ErrorMsg | null => {
        const evListAfter = filterOnNodeIndex(selectedNode, fullEventArray, true);
        const evListToCheckAfter = filterOnCrossNode(evListAfter, evTypeGroup, false);
        if (evListToCheckAfter && evListToCheckAfter.length > 0) {
            const evListToCheckAfterSorted = sortEventsWithoutSecMs(evListToCheckAfter, false);
            if (isDateAfter(checkDateUtc, getDateWithoutSecMs(evListToCheckAfterSorted[0].eventDate.actualDateTimeUtc))) {
                const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);
                errorMsg.error = true;
                errorMsg.message =
                    evTypeName +
                    ' must be before ' +
                    generateEventNameWithDependencyAndDate(evListToCheckAfterSorted[0]) +
                    createNodeName(selectedNode, evListToCheckAfterSorted[0]);
                errorMsg.notValidatedEvent = evListToCheckAfterSorted[0];
                return errorMsg;
            }
        }
        return null;
    };

    const isCrossPortCallConditionOkNew = (
        eventTypeData: EventTypeData,
        fullEventArray: TankersEvent[],
        selectedNode: IRotationNode,
        checkEventType: EventType,
        checkDateUtc: Date
    ): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        const evTypeGroup = getEventTypeGroup(
            eventTypeData,
            checkEventType.eventTypeGroupId,
            checkEventType.eventTypeGroup.eventTypeLevel.eventTypeLevelId
        );
        if (evTypeGroup) {
            const errMsgPrev = crossPortCallConditionPrevious(
                fullEventArray,
                selectedNode,
                getEventTypeName(checkEventType),
                checkDateUtc,
                evTypeGroup
            );
            if (errMsgPrev) return errMsgPrev;
            const errMsgNext = crossPortCallConditionNext(fullEventArray, selectedNode, getEventTypeName(checkEventType), checkDateUtc, evTypeGroup);
            if (errMsgNext) return errMsgNext;
        }
        return errorMsg;
    };

    const isCrossPortCallConditionOkUpdate = (
        eventTypeData: EventTypeData,
        fullEventArray: TankersEvent[],
        selectedNode: IRotationNode,
        checkEvent: TankersEvent,
        checkDateUtc: Date
    ): ErrorMsg => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        if (checkEvent.eventType && checkEvent.eventType.eventTypeGroup && checkEvent.eventType.eventTypeGroup.eventTypeLevel) {
            const evTypeGroup = getEventTypeGroup(
                eventTypeData,
                checkEvent.eventType.eventTypeGroupId,
                checkEvent.eventType.eventTypeGroup.eventTypeLevel.eventTypeLevelId
            );
            if (evTypeGroup) {
                // Check previous port calls
                const errMsgPrev = crossPortCallConditionPrevious(
                    fullEventArray,
                    selectedNode,
                    generateEventNameWithDependency(checkEvent),
                    checkDateUtc,
                    evTypeGroup
                );
                if (errMsgPrev) return errMsgPrev;
                // Check next port calls
                const errMsgNext = crossPortCallConditionNext(
                    fullEventArray,
                    selectedNode,
                    generateEventNameWithDependency(checkEvent),
                    checkDateUtc,
                    evTypeGroup
                );
                if (errMsgNext) return errMsgNext;
            }
        }
        return errorMsg;
    };

    const getLatestAfterDependentFinishedEvent = (
        eventArray: TankersEvent[],
        dependentEvent: TankersEvent,
        node: IRotationNode
    ): TankersEvent | null => {
        const depEvListAll = eventArray.filter(
            (ev) =>
                !getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === dependentEvent.eventTypeId &&
                ev.concurrentSequenceNumber === dependentEvent.concurrentSequenceNumber
        );

        if (depEvListAll && depEvListAll.length > 0) {
            const evListAfterLatestParentSorted = depEvListAll.sort((a, b) => compareActualEventDateTime(a, b, false));
            return evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1];
        }
        return null;
    };

    // Check if event has valid date and time (for new events)
    const isDateTimeForNewEventValid = (
        fullEventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode,
        checkEventType: EventType,
        chDateUtc: Date,
        isRetroactiveModeEnabled?: boolean
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        if (!checkEventType.eventTypeGroup) {
            return errorMsg;
        }

        // Check if we should bypass all validation for retroactive cargo events
        if (isRetroactiveModeEnabled && checkEventType.levelId === RotationTreeEnum.CargoOperation) {
            const mockCheckEvent = {
                eventTypeId: checkEventType.eventTypeId,
                eventLevel: checkEventType.levelId,
                cargoOperationId: (node as any).cargoOperationId,
                portCallId: (node as any).portCallId || (node.portCall ? node.portCall.portCallId : undefined),
            } as unknown as TankersEvent;

            if (isCargoLevelEvent(mockCheckEvent)) {
                return errorMsg; // Bypass all validation
            }
        }

        const checkDateUtc: Date = chDateUtc;
        checkDateUtc.setSeconds(0, 0);

        fullEventArray = fullEventArray.filter((ev) => !ev.deleted);

        const eventArray = getParentEvents(fullEventArray, node);

        // Find events with current context
        const eventArrayInContext: TankersEvent[] = filterOnContext(node, fullEventArray);
        if (eventArrayInContext && eventArrayInContext.length > 0) {
            // Find events with current context, in event type group
            const eventArrayInSequence: TankersEvent[] = eventArrayInContext.filter((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroupId === checkEventType.eventTypeGroupId &&
                    ev.eventTypeGroupSequenceIdentity === checkEventType.eventTypeGroupSequenceIdentity
                );
            });

            const lastEventIndex = findMissingEventTypeInSequence(eventArrayInSequence);
            if (lastEventIndex !== -1) {
                // Check if current event has mandatory predecessors in group
                // If mandatory predecessor exists, date and time of current event must be later than this
                if (isDateAfter(getDateWithoutSecMs(eventArrayInSequence[lastEventIndex].eventDate.actualDateTimeUtc), checkDateUtc)) {
                    errorMsg.error = true;
                    errorMsg.message =
                        getEventTypeName(checkEventType) +
                        ' must be after ' +
                        generateEventNameWithDependencyAndDate(eventArrayInSequence[lastEventIndex]);
                    errorMsg.notValidatedEvent = eventArrayInSequence[lastEventIndex];
                    return errorMsg;
                }
            }
        }

        // If RequiredPreviousTypeId of current event type is set to other event type, date and time of current event must be later than this
        // E.g. check event type Anchor (cargo ops) must be after Arrived
        const reqPrevTypeId = getRequiredPreviousTypeId(checkEventType.eventTypeGroup, node.firstTimeInPortCall);
        if (reqPrevTypeId) {
            errorMsg = isParentSequenceOkForNew(
                eventArray,
                checkEventType,
                eventTypeData,
                checkDateUtc,
                reqPrevTypeId,
                node,
                isRetroactiveModeEnabled
            );
            if (errorMsg.error) {
                return errorMsg;
            }
        }

        if (
            isMultiple(checkEventType.eventTypeGroup, node.firstTimeInPortCall) &&
            !isConcurrent(checkEventType.eventTypeGroup, node.firstTimeInPortCall)
        ) {
            errorMsg = isMultipleNotConcurrentSeqOkNew(eventArray, checkEventType, checkDateUtc, node);
            if (errorMsg.error) {
                return errorMsg;
            }
        }

        // If other events with NotAfterEventTypeId set to current event type, date and time of current event must be after these
        // E.g. check event type Start Of Sea Passage, and other event type group Anchor (cargo ops) have NotAfterEventTypeId set to Start Of Sea Passage
        if (isConcurrent(checkEventType.eventTypeGroup, node.firstTimeInPortCall)) {
            const evDepSeqArray = eventArray.filter(
                (ev: TankersEvent) =>
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    checkEventType.eventTypeGroupSequenceIdentity &&
                    ev.eventTypeGroupSequenceDependencyIdentity === checkEventType.eventTypeGroupSequenceIdentity &&
                    getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                    getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEventType.eventTypeId
            );
            const evDepSeqArraySorted = evDepSeqArray.sort((a, b) => compareActualEventDateTime(a, b, false));
            if (evDepSeqArraySorted && evDepSeqArraySorted.length > 0) {
                const notAfterEv = evDepSeqArraySorted[evDepSeqArraySorted.length - 1];
                if (notAfterEv) {
                    if (isDateAfter(getDateWithoutSecMs(notAfterEv.eventDate.actualDateTimeUtc), checkDateUtc)) {
                        errorMsg.error = true;
                        errorMsg.message = getEventTypeName(checkEventType) + ' must be after ' + generateEventNameWithDependencyAndDate(notAfterEv);
                        errorMsg.notValidatedEvent = notAfterEv;
                        return errorMsg;
                    }
                    const latestEvent = getLatestAfterDependentFinishedEvent(eventArray, notAfterEv, node);
                    if (latestEvent) {
                        if (isDateAfter(getDateWithoutSecMs(latestEvent.eventDate.actualDateTimeUtc), checkDateUtc)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                getEventTypeName(checkEventType) + ' must be after ' + generateEventNameWithDependencyAndDate(latestEvent);
                            errorMsg.notValidatedEvent = latestEvent;
                            return errorMsg;
                        }
                    }
                }
                // Check
                const evListAfterLatestParent = eventArray.filter(
                    (ev) =>
                        !getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                        ev.eventType.eventTypeGroup.requiredPreviousTypeId === notAfterEv.eventTypeId &&
                        ev.concurrentSequenceNumber === checkEventType.parentSequenceNumber
                );
                if (evListAfterLatestParent.length > 0) {
                    const evListAfterLatestParentSorted = evDepSeqArray.sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (
                        isDateAfter(
                            getDateWithoutSecMs(evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1].eventDate.actualDateTimeUtc),
                            checkDateUtc
                        )
                    ) {
                        errorMsg.error = true;
                        errorMsg.message =
                            getEventTypeName(checkEventType) +
                            ' must be after ' +
                            generateEventNameWithDependencyAndDate(evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1]);
                        errorMsg.notValidatedEvent = evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1];
                        return errorMsg;
                    }
                }
            }
        } else {
            const eventTypeArrayHasNotAfter: TankersEvent[] = eventArray.filter((ev: TankersEvent) => {
                if (ev.eventType && ev.eventType.eventTypeGroup) {
                    if (isMultiple(checkEventType.eventTypeGroup, node.firstTimeInPortCall) && checkEventType.eventTypeGroupSequenceIdentity) {
                        if (
                            getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                            getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEventType.eventTypeId &&
                            ev.eventTypeGroupSequenceDependencyIdentity === checkEventType.eventTypeGroupSequenceIdentity
                        )
                            return true;
                    } else {
                        if (
                            getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                            getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEventType.eventTypeId
                        )
                            return true;
                    }
                }
                return false;
            });

            if (eventTypeArrayHasNotAfter && eventTypeArrayHasNotAfter.length > 0) {
                const hasCurrentAsNotAFter: TankersEvent | null =
                    eventTypeArrayHasNotAfter.find((ev) => {
                        return isDateAfter(getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc), checkDateUtc);
                    }) ?? null;
                if (hasCurrentAsNotAFter) {
                    if (!hasNotAfterOk(eventArray, hasCurrentAsNotAFter, node)) {
                        errorMsg.error = true;
                        errorMsg.message =
                            getEventTypeName(checkEventType) + ' must be after ' + generateEventNameWithDependencyAndDate(hasCurrentAsNotAFter);
                        errorMsg.notValidatedEvent = hasCurrentAsNotAFter;
                        return errorMsg;
                    }
                }
            }
        }

        // Check required dependent mandatory event type groups
        checkEventType.eventTypeGroup.requiredConditionalMandatoryArray?.forEach((item) => {
            const errMsg = isParentSequenceOkForNew(eventArray, checkEventType, eventTypeData, checkDateUtc, item.requiredPreviousTypeId, node);
            if (errMsg.error) {
                errorMsg = errMsg;
            }
        });
        if (errorMsg.error) {
            return errorMsg;
        }

        // Check required conditional mandatory
        if (eventArrayInContext && eventArrayInContext.length > 0) {
            const reqArray: RequiredConditionalMandatory[] = [];
            eventArrayInContext.forEach((ev) => {
                if (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.length > 0
                ) {
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.forEach((reqItem) => {
                        const evType = getEventType(reqItem.requiredPreviousTypeId, null);
                        if (evType && evType.eventTypeGroupId === checkEventType.eventTypeGroupId) {
                            const foundReqItem = reqArray.find(
                                (req) => req.requiredConditionalMandatoryId === reqItem.requiredConditionalMandatoryId
                            );
                            if (!foundReqItem) {
                                reqArray.push(reqItem);
                            }
                        }
                    });
                }
            });
            reqArray.forEach((req) => {
                if (!errorMsg.error) {
                    const depEvTypeArray = eventArrayInContext.filter((ev) => ev.eventType && ev.eventType.eventTypeGroupId === req.eventTypeGroupId);
                    if (depEvTypeArray && depEvTypeArray.length > 0) {
                        if (req.notAfterEventTypeId === checkEventType.eventTypeId) {
                            if (
                                isDateBefore(checkDateUtc, getDateWithoutSecMs(depEvTypeArray[depEvTypeArray.length - 1].eventDate.actualDateTimeUtc))
                            ) {
                                errorMsg.error = true;
                                errorMsg.message =
                                    getEventTypeName(checkEventType) +
                                    " must be after  '" +
                                    eventTypeNameOrUnknown(depEvTypeArray[depEvTypeArray.length - 1].eventType) +
                                    "'";
                                errorMsg.notValidatedEvent = depEvTypeArray[depEvTypeArray.length - 1];
                            }
                        }
                    }
                }
            });
        }
        if (errorMsg.error) {
            return errorMsg;
        }

        errorMsg = isCrossPortCallConditionOkNew(eventTypeData, fullEventArray, node, checkEventType, checkDateUtc);
        if (errorMsg.error) {
            return errorMsg;
        }

        if (eventArrayInContext && eventArrayInContext.length > 0) {
            // Find mandatory predecessor event type group in same level and context
            const predecessorEventInGroup: TankersEvent[] = eventArrayInContext.filter((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    ev.eventType.eventTypeGroup.nextGroupInLevelId === checkEventType.eventTypeGroup.eventTypeGroupId
                );
            });
            if (predecessorEventInGroup && predecessorEventInGroup.length > 0) {
                if (isDateAfter(getDateWithoutSecMs(predecessorEventInGroup[0].eventDate.actualDateTimeUtc), checkDateUtc)) {
                    errorMsg.error = true;
                    errorMsg.message =
                        getEventTypeName(checkEventType) + ' must be after ' + generateEventNameWithDependencyAndDate(predecessorEventInGroup[0]);
                    errorMsg.notValidatedEvent = predecessorEventInGroup[0];
                    return errorMsg;
                }
            }
        }
        // CHeck alternate event type groups
        // Check if newEvType has entries in AlternateEventTypeGroupMap
        const altEvTypeGrIdList = eventTypeData.alternateEventTypeGroupMapList
            .filter((aetgm) => aetgm.eventTypeGroupId === checkEventType.eventTypeGroupId)
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        const altEvTypeGrIdListUnique = altEvTypeGrIdList.filter(function (v, i) {
            return altEvTypeGrIdList.indexOf(v) === i;
        });
        if (altEvTypeGrIdListUnique && altEvTypeGrIdListUnique.length > 0) {
            // For each entry in AlternateEventTypeGroupMap
            let ind = 0;
            let keepGoing = true;
            while (keepGoing) {
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUnique[ind]
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        if (isDateAfter(getDateWithoutSecMs(evList[evList.length - 1].eventDate.actualDateTimeUtc), checkDateUtc)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                getEventTypeName(checkEventType) +
                                ' must be after ' +
                                generateEventNameWithDependencyAndDate(evList[evList.length - 1]);
                            errorMsg.notValidatedEvent = evList[evList.length - 1];
                            return errorMsg;
                        }
                    } else {
                        // Check initial event type groups
                        const evListInitial = eventArray
                            .filter((ev) => {
                                return altEvTypeGroupMapList.find((o) => {
                                    return o.initialExcludeEventTypeGroupId && o.initialExcludeEventTypeGroupId === ev.eventType?.eventTypeGroupId;
                                });
                            })
                            .sort((a, b) => compareActualEventDateTime(a, b, false));
                        if (evListInitial && evListInitial.length > 0) {
                            const altEvTypeGroupMapInitial = altEvTypeGroupMapList.find(
                                (o) => o.eventTypeGroupId === checkEventType.eventTypeGroupId
                            );
                            if (altEvTypeGroupMapInitial) {
                                if (
                                    isDateAfter(
                                        getDateWithoutSecMs(evListInitial[evListInitial.length - 1].eventDate.actualDateTimeUtc),
                                        checkDateUtc
                                    )
                                ) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        getEventTypeName(checkEventType) +
                                        ' must be after ' +
                                        generateEventNameWithDependencyAndDate(evListInitial[evListInitial.length - 1]);
                                    errorMsg.notValidatedEvent = evListInitial[evListInitial.length - 1];
                                    return errorMsg;
                                }
                            }
                        }
                    }
                    if (errorMsg.error) keepGoing = false;
                    else {
                        ind++;
                    }
                } else {
                    ind++;
                }
                if (ind === altEvTypeGrIdListUnique.length) keepGoing = false;
            }
        }

        return errorMsg;
    };

    // Check if event has valid date and time (for updating events)
    const isDateTimeForUpdateEventValid = (
        fullEventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        node: IRotationNode,
        checkEvent: TankersEvent,
        chDateUtc: Date,
        isRetroactiveModeEnabled?: boolean
    ): ErrorMsg => {
        let errorMsg: ErrorMsg = new ErrorMsg(false, 'Date+time valid', false, null);

        const checkDateUtc: Date = chDateUtc;
        checkDateUtc.setSeconds(0, 0);

        fullEventArray = fullEventArray.filter((ev) => !ev.deleted);

        errorMsg = isCrossPortCallConditionOkUpdate(eventTypeData, fullEventArray, node, checkEvent, checkDateUtc);
        if (errorMsg.error) {
            return errorMsg;
        }

        const eventArray = getParentEvents(fullEventArray, node);
        // Find events with current context
        const eventArrayInContext: TankersEvent[] = eventArray.filter((ev) => {
            return isEventOnSelectedNode(checkEvent, ev) && getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc);
        });
        if (eventArrayInContext && eventArrayInContext.length > 0) {
            // Find events with current context, in event type group and with same sequence identity
            const eventArrayInGroup: TankersEvent[] = eventArrayInContext.filter((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroupId === checkEvent.eventType.eventTypeGroupId &&
                    ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceIdentity
                );
            });

            if (eventArrayInGroup && eventArrayInGroup.length > 0) {
                // Check if current event has mandatory successors or predecessors in group
                // If mandatory predecessor exists, date and time of current event must be later than this
                // If mandatory succcessor exists, date and time of current event must be before this
                const eventArraySorted = sortOrderedEvents(eventArrayInGroup);
                if (eventArraySorted && eventArraySorted.length > 0) {
                    const checkEventIndex = eventArraySorted.findIndex((ev) => {
                        return ev.eventId === checkEvent.eventId;
                    });
                    // Verify the next event with respect to the check event
                    if (checkEventIndex >= 0 && checkEventIndex < eventArraySorted.length - 1) {
                        const dt = getDateWithoutSecMs(eventArraySorted[checkEventIndex + 1].eventDate.actualDateTimeUtc);
                        const chDt = checkDateUtc;
                        if (isDateAfter(chDt, dt)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) +
                                ' must be before ' +
                                generateEventNameWithDependencyAndDate(eventArraySorted[checkEventIndex + 1]);
                            errorMsg.notValidatedEvent = eventArraySorted[checkEventIndex + 1];
                            return errorMsg;
                        }
                    }

                    // Verify the previous event with respect to the check event
                    if (checkEventIndex > 0) {
                        const dt2 = getDateWithoutSecMs(eventArraySorted[checkEventIndex - 1].eventDate.actualDateTimeUtc);

                        if (isDateAfter(dt2, checkDateUtc)) {
                            const prevLocalTime = eventArraySorted[checkEventIndex - 1].eventDate.actualDateTimeLocal;
                            const checkLocalTime = checkEvent.eventDate?.actualDateTimeLocal;

                            // Check local times as fallback - only fail if local times don't exist or also show wrong order
                            const localTimesShowCorrectOrder =
                                prevLocalTime &&
                                checkLocalTime &&
                                !isDateAfter(getDateWithoutSecMs(prevLocalTime), getDateWithoutSecMs(checkLocalTime));

                            if (!localTimesShowCorrectOrder) {
                                errorMsg.error = true;
                                errorMsg.message =
                                    generateEventNameWithDependency(checkEvent) +
                                    ' must be after ' +
                                    generateEventNameWithDependencyAndDate(eventArraySorted[checkEventIndex - 1]);
                                errorMsg.notValidatedEvent = eventArraySorted[checkEventIndex - 1];
                                return errorMsg;
                            }
                        }
                    }
                }

                // Find mandatory predecessor event type group in same level and context
                const predecessorEventInGroup: TankersEvent[] = eventArrayInContext.filter((ev) => {
                    return (
                        ev.eventType &&
                        ev.eventType.eventTypeGroup &&
                        ev.eventType.eventTypeGroup.nextGroupInLevelId === checkEvent.eventType.eventTypeGroup.eventTypeGroupId
                    );
                });
                if (predecessorEventInGroup && predecessorEventInGroup.length > 0) {
                    if (isDateAfter(getDateWithoutSecMs(predecessorEventInGroup[0].eventDate.actualDateTimeUtc), checkDateUtc)) {
                        // Check if only one event left in the event type group
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) +
                            ' must be after ' +
                            generateEventNameWithDependencyAndDate(predecessorEventInGroup[0]);
                        errorMsg.notValidatedEvent = predecessorEventInGroup[0];
                        return errorMsg;
                    }
                }

                // Find mandatory successor event type group in same level and context
                const successorEventInGroup: TankersEvent[] = eventArrayInContext.filter((ev) => {
                    return (
                        ev.eventType &&
                        ev.eventType.eventTypeGroup &&
                        ev.eventType.eventTypeGroup.eventTypeGroupId === checkEvent.eventType.eventTypeGroup.nextGroupInLevelId
                    );
                });
                if (successorEventInGroup && successorEventInGroup.length === 1) {
                    if (isDateAfter(checkDateUtc, getDateWithoutSecMs(successorEventInGroup[0].eventDate.actualDateTimeUtc))) {
                        // Check if only one event left in the event type group
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) +
                            ' must be before ' +
                            generateEventNameWithDependencyAndDate(successorEventInGroup[0]);
                        errorMsg.notValidatedEvent = successorEventInGroup[0];
                        return errorMsg;
                    }
                }
            }
        }

        // Check required conditional mandatory
        const evDependentOnCheckEventArray = eventArrayInContext.filter(
            (ev) => checkEvent.eventTypeGroupSequenceIdentity === ev.eventTypeGroupSequenceDependencyIdentity
        );
        if (evDependentOnCheckEventArray && evDependentOnCheckEventArray.length > 0) {
            const eventTypeArraySorted = sortOrderedEventTypes(checkEvent.eventType.eventTypeGroup.eventTypeArray);
            const reqArray: RequiredConditionalMandatory[] = [];
            evDependentOnCheckEventArray.forEach((ev) => {
                if (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray &&
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.length > 0
                ) {
                    ev.eventType.eventTypeGroup.requiredConditionalMandatoryArray.forEach((reqItem) => {
                        const foundReqItem = reqArray.find((req) => req.requiredConditionalMandatoryId === reqItem.requiredConditionalMandatoryId);
                        if (!foundReqItem) {
                            reqArray.push(reqItem);
                        }
                    });
                }
            });
            reqArray.forEach((req) => {
                if (!errorMsg.error) {
                    const depEvTypeArray = evDependentOnCheckEventArray.filter((ev) => ev.eventType.eventTypeGroupId === req.eventTypeGroupId);
                    if (depEvTypeArray && depEvTypeArray.length > 0) {
                        const reqPrevIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === req.requiredPreviousTypeId);
                        const notAfterIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === req.notAfterEventTypeId);
                        const checkEventIndex = eventTypeArraySorted.findIndex((ev) => ev.eventTypeId === checkEvent.eventTypeId);

                        if (reqPrevIndex > -1 && notAfterIndex > -1 && checkEventIndex > -1) {
                            if (checkEventIndex === reqPrevIndex) {
                                if (isDateAfter(checkDateUtc, getDateWithoutSecMs(depEvTypeArray[0].eventDate.actualDateTimeUtc))) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        generateEventNameWithDependency(checkEvent) +
                                        ' must be before  ' +
                                        generateEventNameWithDependencyAndDate(depEvTypeArray[0]);
                                    errorMsg.notValidatedEvent = depEvTypeArray[0];
                                }
                            } else if (checkEventIndex === notAfterIndex) {
                                if (
                                    isDateBefore(
                                        checkDateUtc,
                                        getDateWithoutSecMs(depEvTypeArray[depEvTypeArray.length - 1].eventDate.actualDateTimeUtc)
                                    )
                                ) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        generateEventNameWithDependency(checkEvent) +
                                        ' must be after  ' +
                                        generateEventNameWithDependencyAndDate(depEvTypeArray[depEvTypeArray.length - 1]);
                                    errorMsg.notValidatedEvent = depEvTypeArray[depEvTypeArray.length - 1];
                                }
                            }
                        }
                    }
                }
            });
        }
        if (errorMsg.error) {
            return errorMsg;
        }

        // If RequiredPreviousTypeId of current event type is set to other event type, date and time of current event must be later than this
        // E.g. check event type Anchor (cargo ops) must be after Arrived
        const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall);
        if (reqPrevTypeId) {
            errorMsg = isParentSequenceOkForUpdate(
                eventArray,
                checkEvent,
                eventTypeData,
                checkDateUtc,
                reqPrevTypeId,
                getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall),
                node,
                isRetroactiveModeEnabled
            );
            if (errorMsg.error) {
                return errorMsg;
            }
            // checkEvent = Protest
            if (!getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)) {
                const reqParentOnlyAfter = eventArrayInContext.find((ev) => {
                    return (
                        reqPrevTypeId === ev.eventTypeId &&
                        checkEvent.eventTypeGroupSequenceDependencyIdentity &&
                        checkEvent.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                    );
                });
                // reqParentOnlyAfter = Pumping complete
                if (reqParentOnlyAfter && getNotAfterEventTypeId(reqParentOnlyAfter.eventType.eventTypeGroup, node.firstTimeInPortCall)) {
                    // Find cargo hose off
                    const notAfterEvRequired = eventArrayInContext.find((ev) => {
                        return (
                            ev.eventTypeId === getNotAfterEventTypeId(reqParentOnlyAfter.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                            reqParentOnlyAfter.eventTypeGroupSequenceDependencyIdentity &&
                            reqParentOnlyAfter.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                        );
                    });
                    if (notAfterEvRequired && isDateAfter(checkDateUtc, getDateWithoutSecMs(notAfterEvRequired.eventDate.actualDateTimeUtc))) {
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) +
                            ' must be before ' +
                            generateEventNameWithDependencyAndDate(notAfterEvRequired);
                        errorMsg.notValidatedEvent = notAfterEvRequired;
                        return errorMsg;
                    }
                }
            }
        }

        if (
            isMultiple(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
            !isConcurrent(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
        ) {
            errorMsg = isMultipleNotConcurrentSeqOkUpdate(eventArray, checkEvent, checkDateUtc, node);
            if (errorMsg.error) {
                return errorMsg;
            }
        }
        // Check if current event type group has dependency to other event type group
        if (checkEvent.eventTypeGroupSequenceDependencyIdentity) {
            const dependentEventTypeArray: TankersEvent[] = eventArrayInContext.filter((ev) => {
                return ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceDependencyIdentity;
            });
            if (dependentEventTypeArray && dependentEventTypeArray.length > 0) {
                const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall);
                const reqEv = dependentEventTypeArray.find((ev) => ev.eventTypeId === reqPrevTypeId);
                if (reqEv) {
                    if (isDateBefore(checkDateUtc, getDateWithoutSecMs(reqEv.eventDate.actualDateTimeUtc))) {
                        const reqEvLocalTime = reqEv.eventDate.actualDateTimeLocal;
                        const checkLocalTime = checkEvent.eventDate?.actualDateTimeLocal;

                        // Check local times as fallback - only fail if local times don't exist or also show wrong order
                        const localTimesShowCorrectOrder =
                            reqEvLocalTime &&
                            checkLocalTime &&
                            !isDateAfter(getDateWithoutSecMs(reqEvLocalTime), getDateWithoutSecMs(checkLocalTime));

                        if (!localTimesShowCorrectOrder) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) + ' must be after ' + generateEventNameWithDependencyAndDate(reqEv);
                            errorMsg.notValidatedEvent = reqEv;
                            return errorMsg;
                        }
                    }
                }
                const notAfterEv = dependentEventTypeArray.find(
                    (ev) => ev.eventTypeId === getNotAfterEventTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
                );
                if (notAfterEv) {
                    if (isDateAfter(checkDateUtc, getDateWithoutSecMs(notAfterEv.eventDate.actualDateTimeUtc))) {
                        errorMsg.error = true;
                        errorMsg.message =
                            generateEventNameWithDependency(checkEvent) + ' must be before ' + generateEventNameWithDependencyAndDate(notAfterEv);
                        errorMsg.notValidatedEvent = notAfterEv;
                        return errorMsg;
                    }
                }
                const reqCondMand1 = checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray?.find((rcm) => {
                    const reqEv4 = dependentEventTypeArray.find((ev) => rcm.requiredPreviousTypeId === ev.eventTypeId);
                    if (reqEv4) {
                        if (isDateBefore(checkDateUtc, getDateWithoutSecMs(reqEv4.eventDate.actualDateTimeUtc))) {
                            return true;
                        }
                    }
                    return false;
                });
                if (reqCondMand1) {
                    const reqEv3 = dependentEventTypeArray.find((ev) => ev.eventTypeId === reqCondMand1.requiredPreviousTypeId);
                    if (reqEv3) {
                        if (isDateBefore(checkDateUtc, getDateWithoutSecMs(reqEv3.eventDate.actualDateTimeUtc))) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) + ' must be after ' + reqCondMand1.requiredPreviousTypeName + "'";
                            errorMsg.notValidatedEvent = reqEv3;
                            return errorMsg;
                        }
                    }
                }
                const reqCondMand2 = checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray?.find((rcm) => {
                    const notAfter = dependentEventTypeArray.find((ev) => rcm.notAfterEventTypeId === ev.eventTypeId);
                    if (notAfter) {
                        if (isDateAfter(checkDateUtc, getDateWithoutSecMs(notAfter.eventDate.actualDateTimeUtc))) {
                            return true;
                        }
                    }
                    return false;
                });
                if (reqCondMand2) {
                    const reqEv2 = dependentEventTypeArray.find((ev) => ev.eventTypeId === reqCondMand2.requiredPreviousTypeId);
                    if (reqEv2) {
                        if (isDateAfter(checkDateUtc, getDateWithoutSecMs(reqEv2.eventDate.actualDateTimeUtc))) {
                            errorMsg.error = true;
                            errorMsg.message =
                                generateEventNameWithDependency(checkEvent) + " must be before '" + reqCondMand2.notAfterEventTypeName + "'";
                            errorMsg.notValidatedEvent = reqEv2;
                            return errorMsg;
                        }
                    }
                }
            }
        }

        // If RequiredPreviousTypeId of current event type is set to other event type, date and time of current event must be later than this
        // Exclude dependency event type group check here
        if (
            isRequiredEventTypeInSameEventTypeGroup(checkEvent.eventType.eventTypeGroup, node) &&
            !checkEvent.eventTypeGroupSequenceDependencyIdentity
        ) {
            const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType.eventTypeGroup, node.firstTimeInPortCall);
            const requiredPreviousEventType = eventArrayInContext.find((ev) => {
                return ev.eventTypeId === reqPrevTypeId;
            });

            if (requiredPreviousEventType && isDateAfter(getDateWithoutSecMs(requiredPreviousEventType.eventDate.actualDateTimeUtc), checkDateUtc)) {
                errorMsg.error = true;
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) +
                    ' must be after ' +
                    generateEventNameWithDependencyAndDate(requiredPreviousEventType);
                errorMsg.notValidatedEvent = requiredPreviousEventType;
                return errorMsg;
            }
        }

        // If other events with RequiredPreviousTypeId set to current event type, date and time of current event must be after these
        // Only applies to events with eventTypeGroupSequenceDependencyIdentity == null
        const eventTypeArrayHasCurrentRequired: TankersEvent[] = eventArrayInContext.filter((ev) => {
            return (
                ev.eventType &&
                ev.eventType.eventTypeGroup &&
                getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEvent.eventTypeId &&
                ev.eventTypeGroupSequenceDependencyIdentity &&
                ev.eventTypeGroupSequenceDependencyIdentity === checkEvent.eventTypeGroupSequenceIdentity
            );
        });
        if (eventTypeArrayHasCurrentRequired && eventTypeArrayHasCurrentRequired.length > 0) {
            const hasCurrentAsRequired = eventTypeArrayHasCurrentRequired.find((ev) => {
                return isDateAfter(checkDateUtc, getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc));
            });
            if (hasCurrentAsRequired) {
                errorMsg.error = true;
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) + ' cannot be after ' + generateEventNameWithDependencyAndDate(hasCurrentAsRequired);
                errorMsg.notValidatedEvent = hasCurrentAsRequired;
                return errorMsg;
            }
        }

        // If other events with RequiredPreviousTypeId set to current event type, date and time of current event must be before these
        // Only applies to events with eventTypeGroupSequenceDependencyIdentity == null or is dependent on current event type
        const eventTypeArrayHasCurrentRequiredDependent: TankersEvent[] = eventArrayInContext.filter((ev) => {
            return (
                ev.eventType &&
                ev.eventType.eventTypeGroup &&
                getRequiredPreviousTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEvent.eventTypeId &&
                ev.eventTypeGroupSequenceDependencyIdentity &&
                ev.eventTypeGroupSequenceDependencyIdentity === checkEvent.eventTypeGroupSequenceIdentity
            );
        });
        if (eventTypeArrayHasCurrentRequiredDependent && eventTypeArrayHasCurrentRequiredDependent.length > 0) {
            const hasCurrentAsRequired = eventTypeArrayHasCurrentRequiredDependent.find((ev) => {
                return isDateAfter(checkDateUtc, getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc));
            });
            if (hasCurrentAsRequired) {
                errorMsg.error = true;
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) + ' cannot be after ' + generateEventNameWithDependencyAndDate(hasCurrentAsRequired);
                errorMsg.notValidatedEvent = hasCurrentAsRequired;
                return errorMsg;
            }
        }

        // If other events in event type group with NotAfterEventTypeId set to current event type, date and time of current event must be after all these
        // Only applies to events dependent on current event type
        const eventTypeArrayHasNotAfter: TankersEvent[] = eventArrayInContext
            .filter((ev) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroup &&
                    getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) === checkEvent.eventTypeId &&
                    hasEventGroupDependency(ev, checkEvent)
                );
            })
            .sort((a, b) => compareActualEventDateTime(a, b, false));
        if (eventTypeArrayHasNotAfter && eventTypeArrayHasNotAfter.length > 0) {
            const hasCurrentAsNotAFter = eventTypeArrayHasNotAfter.find((ev) => {
                return isDateAfter(getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc), checkDateUtc);
            });
            if (hasCurrentAsNotAFter) {
                const notAfterLocalTime = hasCurrentAsNotAFter.eventDate.actualDateTimeLocal;
                const checkLocalTime = checkEvent.eventDate?.actualDateTimeLocal;

                // Check local times as fallback - only fail if local times don't exist or also show wrong order
                const localTimesShowCorrectOrder =
                    notAfterLocalTime && checkLocalTime && !isDateAfter(getDateWithoutSecMs(notAfterLocalTime), getDateWithoutSecMs(checkLocalTime));

                if (!localTimesShowCorrectOrder) {
                    errorMsg.error = true;
                    errorMsg.message =
                        generateEventNameWithDependency(checkEvent) +
                        ' must be after ' +
                        generateEventNameWithDependencyAndDate(hasCurrentAsNotAFter);
                    errorMsg.notValidatedEvent = hasCurrentAsNotAFter;
                    return errorMsg;
                }
            }
            const notAfterEv = eventTypeArrayHasNotAfter[eventTypeArrayHasNotAfter.length - 1];
            const evListAfterLatestParent = eventArray.filter(
                (ev) =>
                    !getNotAfterEventTypeId(ev.eventType.eventTypeGroup, node.firstTimeInPortCall) &&
                    ev.eventType.eventTypeGroup.requiredPreviousTypeId === notAfterEv.eventTypeId &&
                    ev.concurrentSequenceNumber === checkEvent.concurrentSequenceNumber
            );
            if (evListAfterLatestParent.length > 0) {
                const evListAfterLatestParentSorted = evListAfterLatestParent.sort((a, b) => compareActualEventDateTime(a, b, false));
                if (
                    isDateAfter(
                        getDateWithoutSecMs(evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1].eventDate.actualDateTimeUtc),
                        checkDateUtc
                    )
                ) {
                    errorMsg.error = true;
                    errorMsg.message =
                        generateEventNameWithDependency(checkEvent) +
                        ' must be after ' +
                        generateEventNameWithDependencyAndDate(evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1]);
                    errorMsg.notValidatedEvent = evListAfterLatestParentSorted[evListAfterLatestParentSorted.length - 1];
                    return errorMsg;
                }
            }
        }

        // Check if other event types has current group as required group, only applies if current event is not dependent on any other event type group
        if (!checkEvent.eventTypeGroupSequenceDependencyIdentity) {
            const hasRequiredGroupArray = eventArrayInContext.filter((ev) => {
                if (ev.eventType && ev.eventType.eventTypeGroupRequiredArray) {
                    const evTypeGrReqArray = ev.eventType.eventTypeGroupRequiredArray.filter((etgr) => {
                        return etgr === checkEvent.eventType.eventTypeGroupId;
                    });
                    return evTypeGrReqArray && evTypeGrReqArray.length > 0;
                }
                return false;
            });

            const hasRequiredEvent = hasRequiredGroupArray.find((ev) => {
                return isDateBefore(getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc), checkDateUtc);
            });

            if (hasRequiredEvent) {
                errorMsg.error = true;
                errorMsg.message =
                    generateEventNameWithDependency(checkEvent) + ' must be before ' + generateEventNameWithDependencyAndDate(hasRequiredEvent);
                errorMsg.notValidatedEvent = hasRequiredEvent;
                return errorMsg;
            }
        }

        // Check if current event type has other event type groups as required, applies not to events that are dependent on other event type groups
        if (checkEvent.eventType.eventTypeGroupRequiredArray && checkEvent.eventType.eventTypeGroupRequiredArray.length > 0) {
            const requiredEventTypeGroupId = checkEvent.eventType.eventTypeGroupRequiredArray.find((reqId) => {
                return eventArrayInContext.find((ev) => {
                    return !!(
                        ev.eventType &&
                        ev.eventType.eventTypeGroupId === reqId &&
                        (!ev.eventTypeGroupSequenceDependencyIdentity ||
                            (ev.eventTypeGroupSequenceDependencyIdentity &&
                                ev.eventTypeGroupSequenceDependencyIdentity === checkEvent.eventTypeGroupSequenceIdentity)) &&
                        isDateAfter(getDateWithoutSecMs(ev.eventDate.actualDateTimeUtc), checkDateUtc)
                    );
                });
            });

            if (requiredEventTypeGroupId) {
                const hasRequiredEvent = eventArrayInContext.find((ev) => ev.eventType && ev.eventType.eventTypeGroupId === requiredEventTypeGroupId);
                if (hasRequiredEvent) {
                    errorMsg.error = true;
                    errorMsg.message =
                        generateEventNameWithDependency(checkEvent) +
                        ' must be before all events in event type group ' +
                        generateEventNameWithDependencyAndDate(hasRequiredEvent);
                    errorMsg.notValidatedEvent = hasRequiredEvent;
                    return errorMsg;
                }
            }
        }
        // CHeck alternate event type groups
        // Check if newEvType has entries in AlternateEventTypeGroupMap
        const altEvTypeGrIdList = eventTypeData.alternateEventTypeGroupMapList
            .filter((aetgm) => aetgm.eventTypeGroupId === checkEvent.eventType.eventTypeGroupId)
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        const altEvTypeGrIdListUnique = altEvTypeGrIdList.filter(function (v, i) {
            return altEvTypeGrIdList.indexOf(v) == i;
        });
        if (altEvTypeGrIdListUnique && altEvTypeGrIdListUnique.length > 0) {
            // For each entry in AlternateEventTypeGroupMap
            let ind = 0;
            let keepGoing = true;
            while (keepGoing) {
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUnique[ind]
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        const evInd = evList.findIndex((ev) => ev.eventId === checkEvent.eventId);
                        if (evInd > -1) {
                            if (evInd > 0) {
                                if (isDateAfter(getDateWithoutSecMs(evList[evInd - 1].eventDate.actualDateTimeUtc), checkDateUtc)) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        getEventTypeName(checkEvent.eventType) +
                                        ' must be after ' +
                                        generateEventNameWithDependencyAndDate(evList[evList.length - 1]);
                                    errorMsg.notValidatedEvent = evList[evList.length - 1];
                                    return errorMsg;
                                }
                            }
                            if (evInd < evList.length - 1) {
                                if (isDateBefore(getDateWithoutSecMs(evList[evInd + 1].eventDate.actualDateTimeUtc), checkDateUtc)) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        getEventTypeName(checkEvent.eventType) +
                                        ' must be before ' +
                                        generateEventNameWithDependencyAndDate(evList[evInd + 1]);
                                    errorMsg.notValidatedEvent = evList[evInd + 1];
                                    return errorMsg;
                                }
                            }
                        }
                    } else {
                        // Check initial event type groups
                        const evListInitial = eventArray
                            .filter((ev) => {
                                return altEvTypeGroupMapList.find((o) => {
                                    return o.initialExcludeEventTypeGroupId && o.initialExcludeEventTypeGroupId === ev.eventType?.eventTypeGroupId;
                                });
                            })
                            .sort((a, b) => compareActualEventDateTime(a, b, false));
                        if (evListInitial && evListInitial.length > 0) {
                            const altEvTypeGroupMapInitial = altEvTypeGroupMapList.find(
                                (o) => o.eventTypeGroupId === checkEvent.eventType.eventTypeGroupId
                            );
                            if (altEvTypeGroupMapInitial) {
                                if (
                                    isDateAfter(
                                        getDateWithoutSecMs(evListInitial[evListInitial.length - 1].eventDate.actualDateTimeUtc),
                                        checkDateUtc
                                    )
                                ) {
                                    errorMsg.error = true;
                                    errorMsg.message =
                                        getEventTypeName(checkEvent.eventType) +
                                        ' must be after ' +
                                        generateEventNameWithDependencyAndDate(evListInitial[evListInitial.length - 1]);
                                    errorMsg.notValidatedEvent = evListInitial[evListInitial.length - 1];
                                    return errorMsg;
                                }
                            }
                        }
                    }
                    ind++;
                } else {
                    ind++;
                }
                if (ind === altEvTypeGrIdListUnique.length) keepGoing = false;
            }
        }

        // CHeck parent to alternate event type groups
        const altEvTypeGrIdListToInitial = eventTypeData.alternateEventTypeGroupMapList
            .filter((aetgm) => aetgm.initialExcludeEventTypeGroupId && aetgm.initialExcludeEventTypeGroupId === checkEvent.eventType.eventTypeGroupId)
            .map((o) => o.alternateEventTypeGroupMapIdentity);
        const altEvTypeGrIdListUniqueInitial = altEvTypeGrIdListToInitial.filter(function (v, i) {
            return altEvTypeGrIdListToInitial.indexOf(v) == i;
        });
        if (altEvTypeGrIdListUniqueInitial && altEvTypeGrIdListUniqueInitial.length > 0) {
            // For each entry in AlternateEventTypeGroupMap
            let indInitial = 0;
            let keepGoingInitial = true;
            while (keepGoingInitial) {
                const altEvTypeGroupMapList = eventTypeData.alternateEventTypeGroupMapList.filter(
                    (o) => o.alternateEventTypeGroupMapIdentity === altEvTypeGrIdListUniqueInitial[indInitial] && o.initialExcludeEventTypeGroupId
                );
                if (altEvTypeGroupMapList && altEvTypeGroupMapList.length > 0) {
                    const evList = eventArray
                        .filter((ev) => {
                            return altEvTypeGroupMapList.find((o) => o.eventTypeGroupId === ev.eventType?.eventTypeGroupId);
                        })
                        .sort((a, b) => compareActualEventDateTime(a, b, false));
                    if (evList && evList.length > 0) {
                        if (isDateBefore(getDateWithoutSecMs(evList[0].eventDate.actualDateTimeUtc), checkDateUtc)) {
                            errorMsg.error = true;
                            errorMsg.message =
                                getEventTypeName(checkEvent.eventType) + ' must be before ' + generateEventNameWithDependencyAndDate(evList[0]);
                            errorMsg.notValidatedEvent = evList[0];
                            return errorMsg;
                        }
                    }
                    indInitial++;
                } else {
                    indInitial++;
                }
                if (indInitial === altEvTypeGrIdListUniqueInitial.length) keepGoingInitial = false;
            }
        }

        return errorMsg;
    };

    return {
        isDateTimeForUpdateEventValid,
        isDateTimeForNewEventValid,
    };
};
